import React, { useEffect, useState } from 'react';
import '../css/PasswordReset.css';
import logo from '../images/logo.png';
import { useParams } from 'react-router-dom';
import { accessReset, resetPassword } from '../Services/Api';
import Swal from "sweetalert2";

const PasswordReset = () => {
    const { id } = useParams();
    const [tokenStatus, setTokenStatus] = useState(null);
    const [formData, setFormData] = useState({
        newPassword: '',
        confirmPassword: ''
    });
    const [showPassword, setShowPassword] = useState({
        newPassword: false,
        confirmPassword: false
    });
    const [passwordStrength, setPasswordStrength] = useState(0);
    const [requirements, setRequirements] = useState({
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false
    });
    const [passwordMatch, setPasswordMatch] = useState(null);
    const [message, setMessage] = useState({ type: '', text: '', show: false });

    // 🔹 Verify token on page load
    useEffect(() => {
         document.title = 'Password Reset';
        const verifyToken = async () => {
            try {
                const response = await accessReset(id);
                // console.log(response);
                setTokenStatus(response.tokenStatus);

                // Expect API response like { status: "success", message: "Valid token" }
                // if (response.status === "success") {
                //     setTokenStatus(response.tokenStatus);
                // } else {
                //     setTokenStatus(response.tokenStatus); // or "used" depending on backend response
                // }
            } catch (err) {
                setTokenStatus("expired");
                console.error("Token verify error:", err.message);
            }
        };
        verifyToken();
    }, [id]);

    const togglePasswordVisibility = (field) => {
        setShowPassword(prev => ({ ...prev, [field]: !prev[field] }));
    };

    const checkPasswordStrength = (password) => {
        const hasLength = password.length >= 8;
        const hasUppercase = /[A-Z]/.test(password);
        const hasLowercase = /[a-z]/.test(password);
        const hasNumber = /[0-9]/.test(password);
        const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);

        setRequirements({ length: hasLength, uppercase: hasUppercase, lowercase: hasLowercase, number: hasNumber, special: hasSpecial });

        let strength = 0;
        if (hasLength) strength += 20;
        if (hasUppercase) strength += 20;
        if (hasLowercase) strength += 20;
        if (hasNumber) strength += 20;
        if (hasSpecial) strength += 20;

        setPasswordStrength(strength);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        if (name === 'newPassword') checkPasswordStrength(value);

        if (name === 'confirmPassword' || (name === 'newPassword' && formData.confirmPassword)) {
            const match = name === 'newPassword'
                ? value === formData.confirmPassword
                : value === formData.newPassword;
            setPasswordMatch(match);
        }
    };

    const getStrengthColor = () => {
        if (passwordStrength < 40) return '#e74c3c';
        if (passwordStrength < 80) return '#f39c12';
        return '#27ae60';
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        formData.token = id;
        if (formData.newPassword !== formData.confirmPassword) {
            setMessage({ type: 'error', text: 'Passwords do not match.', show: true });
            return;
        }
        if (passwordStrength < 60) {
            setMessage({ type: 'error', text: 'Password is too weak.', show: true });
            return;
        }
        const submitFun = async () => {
            try {
                const response = await resetPassword(formData);
                if (response.status == 'success') {
                    setMessage({ type: 'success', text: 'Password reset successfully!', show: true });
                    Swal.fire({
                        title: "Success!",
                        text: "Password Reset Successfully.",
                        icon: "success",
                        confirmButtonText: "OK",
                    }).then((result) => {
                        if (result.isConfirmed) {
                            // Refresh the page
                            window.location.reload();
                        }
                    });


                }

            } catch (err) {
                setMessage({ type: 'error', text: 'Password is too weak.', show: true });

                console.log(err);

            }
        }

        submitFun();

        // console.log('Password reset successful', formData);
        // 🔹 Call API here to save new password
    };

    // 🔹 Conditional left content
    const renderLeftContent = () => {
        if (tokenStatus === 'expired') {
            return (
                <div className="pwr-message-box expired">
                    <i className="fas fa-exclamation-triangle"></i>
                    <h2>Link Expired</h2>
                    <p>Sorry, this password reset link has expired. Please request a new one.</p>
                </div>
            );
        }
        if (tokenStatus === 'invalid') {
            return (
                <div className="pwr-message-box expired">
                    <i className="fas fa-exclamation-triangle"></i>
                    <h2>Link Expired</h2>
                    <p>Sorry, this password reset link has expired. Please request a new one.</p>
                </div>
            );
        }

        if (tokenStatus === 'used') {
            return (
                <div className="pwr-message-box used">
                    <i className="fas fa-info-circle"></i>
                    <h2>Link Already Used</h2>
                    <p>This password reset link has already been used. Please request a new one if needed.</p>
                </div>
            );
        }

        if (tokenStatus === 'valid') {
            return (
                <form className="pwr-form" onSubmit={handleSubmit}>
                    <div className="pwr-form-group">
                        <label htmlFor="pwr-new-password" className="pwr-label">New Password</label>
                        <div className="pwr-input-container">
                            <input
                                type={showPassword.newPassword ? 'text' : 'password'}
                                id="pwr-new-password"
                                name="newPassword"
                                value={formData.newPassword}
                                onChange={handleInputChange}
                                placeholder="Enter your new password"
                                className="pwr-input"
                            />
                            <input type="hidden" name="token" value={id} />
                            <button type="button" className="pwr-toggle-password" onClick={() => togglePasswordVisibility('newPassword')}>
                                <i className={`fas ${showPassword.newPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                            </button>
                        </div>
                        <div className="pwr-password-strength">
                            <div className="pwr-strength-meter" style={{ width: `${passwordStrength}%`, backgroundColor: getStrengthColor() }}></div>
                        </div>
                    </div>

                    <div className="pwr-form-group">
                        <label htmlFor="pwr-confirm-password" className="pwr-label">Confirm Password</label>
                        <div className="pwr-input-container">
                            <input
                                type={showPassword.confirmPassword ? 'text' : 'password'}
                                id="pwr-confirm-password"
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleInputChange}
                                placeholder="Confirm your new password"
                                className="pwr-input"
                            />
                            <button type="button" className="pwr-toggle-password" onClick={() => togglePasswordVisibility('confirmPassword')}>
                                <i className={`fas ${showPassword.confirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                            </button>
                        </div>
                        {passwordMatch !== null && formData.confirmPassword && (
                            <div className={`pwr-password-requirements ${passwordMatch ? 'pwr-met' : ''}`}>
                                <i className={`fas ${passwordMatch ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
                                {passwordMatch ? 'Passwords match' : 'Passwords do not match'}
                            </div>
                        )}
                    </div>

                    <button type="submit" className="pwr-submit-btn">Reset Password</button>

                    {message.show && (
                        <div className={`pwr-message pwr-${message.type}`}>
                            <i className={`fas ${message.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
                            {message.text}
                        </div>
                    )}

                    <div className="pwr-back-to-login">
                        <p>Remember your password? <a href="#" className="pwr-link">Back to Login</a></p>
                    </div>
                </form>
            );
        }

        return null; // while loading
    };

    return (
        <div className="pwr-container">
            <div className="pwr-wrapper">
                <div className="pwr-form-section">
                    <div className="pwr-logo-section">
                        <img src={logo} alt="Logo" className="pwr-logo-img" />
                    </div>
                    <div className="pwr-header">
                        {tokenStatus === 'valid' && <h1 className="pwr-title">Reset Password</h1>}
                        {tokenStatus === 'valid' && <p className="pwr-subtitle">Create a new password for your account</p>}
                    </div>
                    {renderLeftContent()}
                </div>

                <div className="pwr-image-section">
                    <div className="pwr-icon-container">
                        <i className="fas fa-lock"></i>
                    </div>
                    <div className="pwr-image-content">
                        <h2 className="pwr-image-title">Secure Your Account</h2>
                        <p className="pwr-image-text">
                            Create a strong, unique password to protect your personal information and keep your account secure.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PasswordReset;
